import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import rehypeRaw from 'rehype-raw';
import ReactMarkdown from 'react-markdown';
import { white, universalStyles, disabledGrey, yellow } from '../../../app/theme';
import { ImageFormat, LearningCheckFormat } from '../../../constants/learningCheck.constants';
import { BASE_URL } from '../../../constants/paths.constants';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  questionBox: {
    width: '100%',
    padding: '30px 0',
    borderBottom: `1px solid ${disabledGrey}`,
  },
  questionRow: {
    display: 'flex',
  },
  number: {
    color: white,
    fontSize: 18,
    height: 28,
    width: 28,
    minWidth: 28,
    borderRadius: 14,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: `1px solid ${yellow}`,
    marginRight: 10,
  },
  question: {
    marginTop: 6,
    fontSize: 18,
    color: white,
    fontWeight: 'bold',
    paddingBottom: 8,
    '& > p': {
      margin: 0,
    },
  },
  answerButton: {
    color: yellow,
    fontWeight: 'bold',
    marginLeft: 42,
    cursor: 'pointer',
  },
  buttonText: {
    textDecoration: 'underline',
  },
  correctText: {
    fontSize: 18,
    color: white,
    margin: '8px 0 0 50px',
    '& > p': {
      margin: 0,
    },
  },
  imageRow: {
    maxWidth: '70%',
    margin: 'auto',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 40,
  },
  imageWrapper: {
    flex: 1,
    marginLeft: 57,
    '&:nth-of-type(1)': {
      margin: 0,
    },
  },
  image: {
    maxWidth: '100%',
    height: 'auto',
  },
  caption: {
    color: white,
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
    maxWidth: '80%',
    margin: 'auto',
  },
}));

interface SingleQuestionReviewProps {
  thisQuestion: LearningCheckFormat;
}

const SingleQuestionReview = ({ thisQuestion }: SingleQuestionReviewProps): JSX.Element => {
  const classes = useStyles();
  const [checkedAnswer, setCheckedAnswer] = useState(false);

  const { number, question, correct, comprehensiveCorrect, comprehensiveQuestion } = thisQuestion;
  const { questionText } = question;
  const { correctText, images, captions } = correct;
  const { comprehensiveText, comprehensiveImages, comprehensiveCaptions } = comprehensiveCorrect || {};

  return (
    <div className={classes.questionBox}>
      <div className={classes.questionRow}>
        <div className={classes.number}>
          <span>{number}</span>
        </div>
        <ReactMarkdown rehypePlugins={[rehypeRaw]} className={classes.question}>
          {comprehensiveQuestion || questionText}
        </ReactMarkdown>
      </div>
      <Typography onClick={() => setCheckedAnswer(!checkedAnswer)} className={classes.answerButton}>
        {checkedAnswer ? '-  ' : '+  '}
        <span className={classes.buttonText}>Answer</span>
      </Typography>
      {checkedAnswer && (
        <>
          <ReactMarkdown rehypePlugins={[rehypeRaw]} className={classes.correctText}>
            {comprehensiveText || correctText}
          </ReactMarkdown>
          {(comprehensiveImages || (images && !comprehensiveCorrect)) && (
            <div className={classes.imageRow}>
              {((comprehensiveImages || images) as ImageFormat[]).map((image, index) => (
                <div className={classes.imageWrapper} key={image.path}>
                  <img className={classes.image} src={`${BASE_URL}${image.path}`} alt={image.alt} />
                  {(comprehensiveCaptions || captions) && (
                    <ReactMarkdown rehypePlugins={[rehypeRaw]} className={classes.caption}>
                      {((comprehensiveCaptions || captions) as string[])[index]}
                    </ReactMarkdown>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SingleQuestionReview;
